/**
 * useCompanyInfo — fetches the real Odoo company (name, logo, address, phone,
 * tax id …) once and merges it over the static COMPANY defaults in
 * companyConfig.js. Used by the print/receipt screens so they show the actual
 * company branding instead of the placeholder shipped in the config.
 *
 * The result is cached at module level, so it's fetched only once per session
 * no matter how many print screens are opened.
 */
import { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { COMPANY } from '@/lib/companyConfig';

let _cache = null;       // merged company object once resolved
let _inflight = null;    // shared promise while the request is in flight

/** Merge backend payload over presentation-only defaults.
 *  IMPORTANT: company identity fields (name, address, phone, tax …) come ONLY
 *  from Odoo — we never fall back to the placeholder config for them, because
 *  that would print a fake address / tax number. We only keep neutral
 *  presentation defaults (currency symbol, receipt wording, vat rate, and a
 *  generic logo when the company has none). */
function mergeCompany(d) {
  if (!d) return COMPANY; // endpoint unreachable → last-resort defaults
  return {
    ...COMPANY,                              // keep currencySymbol, vatRate, receipt
    name: d.name || COMPANY.name,
    legalForm: '',                           // Odoo has no legal-form field → never show fake
    logoUrl: d.logo || COMPANY.logoUrl,      // generic app icon if no company logo
    phone: d.phone || '',
    mobile: d.mobile || '',
    email: d.email || '',
    website: d.website || '',
    taxId: d.vat || '',
    commercialReg: d.company_registry || '',
    currencySymbol: d.currency_symbol || COMPANY.currencySymbol,
    // Odoo doesn't send it (the default stays); SellGo's own database does.
    vatRate: d.vat_rate ?? COMPANY.vatRate,
    address: {
      street: d.street || '',
      street2: d.street2 || '',
      city: d.city || '',
      region: d.state || '',
      country: d.country || '',
      postalCode: d.zip || '',
    },
  };
}

/** Forget the cached company (after the manager changes its settings). */
export function resetCompanyInfoCache() {
  _cache = null;
}

export function useCompanyInfo() {
  const [company, setCompany] = useState(_cache || COMPANY);

  useEffect(() => {
    if (_cache) { setCompany(_cache); return; }
    let alive = true;
    (async () => {
      try {
        if (!_inflight) _inflight = api.get('/sellgo_company');
        const res = await _inflight;
        const merged = mergeCompany(res?.success ? res.data : null);
        _cache = merged;
        if (alive) setCompany(merged);
      } catch {
        if (alive) setCompany(COMPANY);
      } finally {
        _inflight = null;
      }
    })();
    return () => { alive = false; };
  }, []);

  return company;
}

/** Build a single-line address string from a company object. */
export function companyAddress(c) {
  const a = (c && c.address) || {};
  return [
    a.street,
    a.street2,
    [a.city, a.postalCode].filter(Boolean).join(' '),
    a.region,
    a.country,
  ].filter(Boolean).join('، ');
}
